// --- added by fix: sanitize UI labels (bidi/control chars) ---
function sanitizeUiLabel(input: string) {
  return input
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, '')
    .replace(/[\u202A-\u202E\u2066-\u2069\u200E\u200F\u061C]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}
// --- end sanitize ---
const ALNUM_RE = /^[A-Za-z0-9]+$/
const TOKEN_MIN_LEN = 5
const TOKEN_MAX_LEN = 10
const HAS_DIGIT_RE = /\d/
const HAS_LOWER_RE = /[a-z]/
const HAS_UPPER_RE = /[A-Z]/
const DIACRITICS_RE = /[\u0300-\u036f]/g

const ALLOWLIST = new Set(
  [
    'all',
    'sports',
    'sport',
    'science',
    'music',
    'news',
    'movies',
    'movie',
    'tv',
    'gaming',
    'games',
    'game',
    'tech',
    'technology',
    'business',
    'finance',
    'politics',
    'world',
    'health',
    'travel',
    'food',
    'art',
    'culture',
    'entertainment',
    'live',
    'podcast',
    'podcasts',
    'show',
    'shows',
    'series',
    'local',
    'trending',
    'popular',
    'breaking',
    'education',
    'history',
    'nature',
    'kids',
    'family',
    'musique',
    'nouvelles',
    'actualite',
    'actualites',
    'politique',
    'technologie',
    'jeux',
    'divertissement',
    'monde',
    'sante',
    'voyage',
    'voyages',
    'nourriture',
    'histoire',
    'education',
    'famille',
    'enfants',
  ].map(label => label.toLowerCase()),
)

const warnedTokenLabels = new Set<string>()

function shouldAllowDebugLabels() {
  if (typeof __DEV__ === 'undefined' || !__DEV__) return false
  try {
    const storage = globalThis?.localStorage
    return storage?.getItem('DEBUG_UI') === '1'
  } catch {
    return false
  }
}

export function isTokenLikeLabel(raw: string): boolean {
  const trimmed = raw.trim()
  if (!trimmed) return false

  // Heuristic for opaque "slug" labels we don't want to show in UI.
  // Handles tokens with or without digits (e.g. GcibpE, YBhZdv, 596DXR).
  if (trimmed.length < TOKEN_MIN_LEN || trimmed.length > TOKEN_MAX_LEN)
    return false
  if (!ALNUM_RE.test(trimmed)) return false

  let normalized = trimmed
  try {
    normalized = trimmed.normalize('NFD').replace(DIACRITICS_RE, '')
  } catch {
    normalized = trimmed
  }
  if (ALLOWLIST.has(normalized.toLowerCase())) return false

  const hasDigit = HAS_DIGIT_RE.test(trimmed)
  const hasLower = HAS_LOWER_RE.test(trimmed)
  const hasUpper = HAS_UPPER_RE.test(trimmed)
  const upperCount = (trimmed.match(/[A-Z]/g) || []).length

  // strong signals
  if (hasDigit) return true
  if (hasLower && hasUpper) return true
  if (upperCount >= 3) return true

  return false
}

export function getSafeUiLabel(
  raw: string | null | undefined,
  options?: {
    fallback?: string | null
    context?: string
  },
): string | null {
  const fallback = options?.fallback ?? null

  const sanitized = typeof raw === 'string' ? sanitizeUiLabel(raw) : ''
  if (!sanitized) return fallback

  if (!isTokenLikeLabel(sanitized)) return sanitized

  const allowDebug = shouldAllowDebugLabels()
  const context = options?.context ?? 'unknown'

  // In production, ALWAYS hide token-like labels unless explicitly allowed via DEBUG_UI.
  // In dev, hide only when a context is provided (so we can tune callers without breaking debugging).
  const isDev = typeof __DEV__ !== 'undefined' ? !!__DEV__ : false
  const shouldFilterHere = !allowDebug && ((!isDev) || (context != 'unknown'))

  if (!shouldFilterHere) return sanitized

  if (!allowDebug && typeof __DEV__ !== 'undefined' && __DEV__) {
    const warnKey = `${context}:${sanitized}`
    if (!warnedTokenLabels.has(warnKey)) {
      warnedTokenLabels.add(warnKey)
      console.warn('[ui-label] Hiding token-like label', {
        label: sanitized,
        context,
      })
    }
  }


  return allowDebug ? sanitized : fallback
}
